import { Modal } from 'core/components/ui/Modal'
import React, { useEffect, useState } from 'react'
import { INTERNAL_ROLES, User, UserRole } from '../../domain/entities/User'

interface UserFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (
    data: Omit<User, 'id' | 'keycloakId' | 'status' | 'createdAt' | 'updatedAt'> & { password?: string },
  ) => Promise<void>
  user?: User | null
}

const roleLabels: Record<UserRole, string> = {
  admin: 'Admin (Full Access)',
  cxo: 'CXO (Executive)',
  supervisor: 'Supervisor (Admin)',
  finance: 'Finance Officer',
  account_manager: 'Account Manager',
  customer_support: 'Customer Support',
  fpo: 'FPO ',
  vlcc: 'VLCC',
  retailer: 'Retailer',
}

// fpo/vlcc/retailer accounts are onboarded externally through the KYC flow, not this form —
// the backend's register endpoint rejects those roles here, so new profiles are restricted
// to internal roles. Editing an existing (already-onboarded) external user still shows its
// real role, it just isn't offered as a choice when creating a new profile.

const PHONE_REGEX = /^\d{10}$/
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const NON_DIGIT_REGEX = /\D/g

const addValidationError = (errorMap: Record<string, string>, field: string, message: string) => {
  errorMap[field] = message
}

const generateRandomPassword = (): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
  const randomValues = new Uint32Array(12)
  window.crypto.getRandomValues(randomValues)
  let newPassword = ''
  for (let i = 0; i < 12; i++) {
    newPassword += chars.charAt(randomValues[i] % chars.length)
  }

  return newPassword
}

interface ValidateUserFormArgs {
  name: string
  phone: string
  email: string
  password: string
  isNewUser: boolean
}

const validateUserForm = ({
  name,
  phone,
  email,
  password,
  isNewUser,
}: ValidateUserFormArgs): Record<string, string> => {
  const errors: Record<string, string> = {}

  // Full Name Validation
  if (!name.trim()) {
    addValidationError(errors, 'name', 'Full name is required.')
  }

  // Phone Number Validation
  if (!phone.trim()) {
    addValidationError(errors, 'phone', 'Phone number is required.')
  } else if (!PHONE_REGEX.test(phone.trim())) {
    addValidationError(errors, 'phone', 'Phone number must be exactly 10 digits.')
  } else {
    // Valid phone number
  }

  // New account specific validations
  if (isNewUser) {
    if (!email.trim()) {
      addValidationError(errors, 'email', 'Email address is required.')
    } else if (!EMAIL_REGEX.test(email.trim())) {
      addValidationError(errors, 'email', 'Enter a valid email address.')
    } else {
      // Valid email address
    }

    if (!password) {
      addValidationError(errors, 'password', 'Password is required for new accounts.')
    } else if (password.length < 6) {
      addValidationError(errors, 'password', 'Password must be at least 6 characters long.')
    } else {
      // Valid password
    }
  }

  return errors
}

export function UserFormModal({ isOpen, onClose, onSubmit, user = null }: UserFormModalProps) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<UserRole>(INTERNAL_ROLES[0])
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const roleOptions = INTERNAL_ROLES

  // Sync state if user prop changes
  useEffect(() => {
    if (user) {
      setName(user.name)
      setPhone(user.phone)
      setEmail(user.email || '')
      setRole(user.role)
    } else {
      setName('')
      setPhone('')
      setEmail('')
      setRole(INTERNAL_ROLES[0])
    }

    setPassword('')
    setShowPassword(false)
    setErrorMsg('')
    setErrors({})
  }, [user, isOpen])

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    const newErrors = validateUserForm({
      name,
      phone,
      email,
      password,
      isNewUser: !user,
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSubmitting(true)
    setErrorMsg('')
    setErrors({})
    try {
      await onSubmit({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        role,
        password: user ? undefined : password,
      })
      onClose()
    } catch (err: unknown) {
      const errorVal = err as Error
      setErrorMsg(errorVal.message || 'An error occurred during submission.')
    } finally {
      setSubmitting(false)
    }
  }

  const submitButtonText = user ? 'Save Changes' : 'Create Profile'

  const footerActions = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={submitting}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="user-form"
        disabled={submitting}
        className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors disabled:opacity-50 min-w-[5rem] flex items-center justify-center"
      >
        {submitting ? (
          <svg className="animate-spin h-4 w-4 text-primary-foreground" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          submitButtonText
        )}
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? 'Edit User Profile' : 'Register New User'}
      footer={footerActions}
      size="md"
    >
      <form id="user-form" onSubmit={handleSubmit} className="space-y-4">
        {errorMsg && (
          <div className="p-3 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
            {errorMsg}
          </div>
        )}

        {/* Full Name */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
            Full Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (errors.name) {
                setErrors((prev) => {
                  const next = { ...prev }
                  delete next.name
                  return next
                })
              }
            }}
            placeholder="John Doe"
            className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none transition-colors ${
              errors.name ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            }`}
          />
          {errors.name && <span className="text-xs text-danger font-medium mt-1 block">{errors.name}</span>}
        </div>

        {/* Contact Fields */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Phone */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
              Phone Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={10}
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value.replace(NON_DIGIT_REGEX, '').slice(0, 10))
                if (errors.phone) {
                  setErrors((prev) => {
                    const next = { ...prev }
                    delete next.phone
                    return next
                  })
                }
              }}
              placeholder="9876543210"
              className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none transition-colors ${
                errors.phone ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
              }`}
            />
            {errors.phone && <span className="text-xs text-danger font-medium mt-1 block">{errors.phone}</span>}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
              Email Address {!user && <span className="text-danger">*</span>}
            </label>
            <input
              type="email"
              value={email}
              disabled={!!user}
              onChange={(e) => {
                setEmail(e.target.value)
                if (errors.email) {
                  setErrors((prev) => {
                    const next = { ...prev }
                    delete next.email
                    return next
                  })
                }
              }}
              placeholder="name@fieldtrack.com"
              className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                errors.email ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
              }`}
            />
            {errors.email && <span className="text-xs text-danger font-medium mt-1 block">{errors.email}</span>}
            {user && <span className="text-xs text-text-secondary mt-1 block">Email can&apos;t be changed.</span>}
          </div>
        </div>

        {/* Role Selection */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider">Role</label>
          {!INTERNAL_ROLES.includes(role) ? (
            <div>
              <input
                type="text"
                value={roleLabels[role] || role}
                disabled
                className="w-full px-3.5 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <span className="text-xs text-text-secondary mt-1 block">
                Role cannot be changed for external accounts.
              </span>
            </div>
          ) : (
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="w-full px-3 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm focus:border-primary focus:outline-none transition-colors"
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {roleLabels[r]}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Password (Only show on register new user) */}
        {!user && (
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
              Password (Set Initial Credential) <span className="text-danger">*</span>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    if (errors.password) {
                      setErrors((prev) => {
                        const next = { ...prev }
                        delete next.password
                        return next
                      })
                    }
                  }}
                  placeholder="Min 6 characters"
                  className={`w-full pl-3.5 pr-10 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none transition-colors ${
                    errors.password ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors focus:outline-none"
                >
                  {showPassword ? (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newPass = generateRandomPassword()
                  setPassword(newPass)
                  setShowPassword(true)
                }}
                className="px-3.5 py-2 border border-border rounded-btn bg-surface hover:bg-bg text-text-primary hover:border-primary text-sm font-semibold transition-all flex items-center gap-1.5 active:scale-95 shadow-sm"
              >
                <svg
                  className="h-4 w-4 text-primary animate-pulse"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate
              </button>
            </div>
            {errors.password && <span className="text-xs text-danger font-medium mt-1 block">{errors.password}</span>}
            <p className="text-xs text-text-secondary">This password will be emailed to the new user.</p>
          </div>
        )}
      </form>
    </Modal>
  )
}
